import { useState } from 'react';
import {
  AuthConflictError,
  linkEmailAccount,
  linkGoogleAccount,
  resolveAuthConflict,
  sendPasswordReset,
  signInWithEmail,
} from '../services/auth';
import { trackEvent } from '../services/analytics';
import rockImg from '../assets/rock.png';
import paperImg from '../assets/paper.png';
import scissorsImg from '../assets/scissors.png';
import './AuthModal.css';

// The single account-upgrade/sign-in surface for the whole app, reached from
// three contexts that share everything except copy and framing:
//   - 'post-tournament': fired once, the moment a guest's first tournament
//     ends (win or elimination) — see useAnonymousUpgradePrompt.
//   - 'nav-prompt': opened any time from the persistent AnonUpgradeBadge, the
//     safety net for someone who dismissed the post-tournament prompt (or
//     never triggered it) and changes their mind later.
//   - 'sign-in': also from AnonUpgradeBadge, for a returning user who already
//     has an account and just wants to sign into it — independent of the
//     upgrade/link flow.
// Keeping these as one component with a variant prop (rather than three
// separate modals) means the linking calls and conflict handling only exist
// in one place — a bug in either only needs fixing once.
function AuthModal({ variant, won, myResult, defaultUsername, onClose, onAuthenticated }) {
  const [formMode, setFormMode] = useState(variant === 'sign-in' ? 'signin' : 'register');
  const [username, setUsername] = useState(defaultUsername || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(null);
  const [resetSent, setResetSent] = useState(false);

  const isRegister = formMode === 'register';

  const toggleMode = () => {
    setError('');
    setResetSent(false);
    setFormMode(isRegister ? 'signin' : 'register');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (isRegister) {
        const user = await linkEmailAccount(email, password, username);
        trackEvent('link_account', { method: 'password' });
        onAuthenticated(user);
      } else {
        const user = await signInWithEmail(email, password);
        trackEvent('email_sign_in');
        onAuthenticated(user);
      }
    } catch (err) {
      if (err instanceof AuthConflictError) {
        setConflict(err);
      } else {
        console.error(err);
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleGoogleClick = async () => {
    setBusy(true);
    setError('');
    try {
      const user = await linkGoogleAccount();
      trackEvent('link_account', { method: 'google' });
      onAuthenticated(user);
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // User backed out of the popup — not an error worth surfacing.
      } else if (err instanceof AuthConflictError) {
        setConflict(err);
      } else {
        console.error(err);
        setError('Could not sign in with Google. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleResolveConflict = async () => {
    setBusy(true);
    try {
      const user = await resolveAuthConflict(conflict);
      trackEvent('resolve_auth_conflict', { method: conflict.method });
      onAuthenticated(user);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not sign in. Please try again.');
      setConflict(null);
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Enter your email first.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await sendPasswordReset(email.trim());
      setResetSent(true);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not send reset email. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (conflict) {
    const label = conflict.method === 'google' ? 'This Google account' : 'This email';
    return (
      <div className="auth-modal-overlay">
        <div className="card auth-modal auth-modal-conflict">
          <h3>Already have an account?</h3>
          <p className="hint">
            {label} already has an account. Sign in instead? Your current guest session's history
            won't carry over to it.
          </p>
          {error && <p className="error">{error}</p>}
          <button className="btn-primary" onClick={handleResolveConflict} disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in to existing account'}
          </button>
          <button className="btn-ghost" onClick={() => setConflict(null)} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  let header;
  let hint;
  if (!isRegister) {
    header = 'Sign in with email';
    hint = 'Sign back into an account you created on this or another device.';
  } else if (variant === 'post-tournament') {
    header = won ? 'You won! Save your progress?' : 'Save your progress?';
    hint = won
      ? `Create a free account to keep your ${myResult?.pointsAwarded ?? ''} points and show up on the global leaderboard.`
      : 'Create a free account so your points and match history are still here next time you play.';
  } else {
    header = 'Create your account';
    hint = 'Save your points and show up on the global leaderboard.';
  }

  return (
    <div className="auth-modal-overlay">
      <div className="card auth-modal">
        {variant === 'post-tournament' && isRegister && (
          <div className="auth-modal-icons">
            <img src={rockImg} alt="" />
            <img src={paperImg} alt="" />
            <img src={scissorsImg} alt="" />
          </div>
        )}
        <h3>{header}</h3>
        <p className="hint">{hint}</p>

        <button className="btn-ghost" onClick={handleGoogleClick} disabled={busy}>
          🔐 Continue with Google
        </button>

        <div className="divider-or">
          <span className="divider-or-line" />
          <span className="divider-or-label">OR</span>
          <span className="divider-or-line" />
        </div>

        <form onSubmit={handleSubmit}>
          {isRegister && (
            <input
              className="input-field"
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              maxLength={20}
              disabled={busy}
              required
            />
          )}
          <input
            className="input-field"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            required
          />
          <input
            className="input-field"
            type="password"
            placeholder="Password (6+ characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={6}
            disabled={busy}
            required
          />
          {!isRegister && (
            <button className="auth-modal-forgot" type="button" onClick={handleForgotPassword} disabled={busy}>
              Forgot password?
            </button>
          )}
          {resetSent && !isRegister && <p className="hint">Reset email sent — check your inbox.</p>}
          {error && <p className="error">{error}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? (isRegister ? 'Creating account…' : 'Signing in…') : isRegister ? '🔐 Create Account' : '🔐 Sign In'}
          </button>
        </form>

        <button className="auth-modal-switch" type="button" onClick={toggleMode} disabled={busy}>
          {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
        </button>
        <button className="btn-ghost" onClick={onClose} disabled={busy}>
          {isRegister ? 'Maybe later — keep playing anonymously' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}

export default AuthModal;
